/* eslint complexity: ["error", {max: 17}] -- this applies for the entire file */
import { BottomSheetTextInput } from "@gorhom/bottom-sheet";
import isNil from "lodash/isNil";
import { Platform } from "react-native";
import type { TextInputProps } from "react-native";
import { TextArea as BaseTextArea, styled } from "tamagui";

import { TextTestID } from "@/constants/TestID";
import { TextAreaDefaultHeight } from "@/constants/UI";
import Text from "@/ui/core/Text";
import { getBorderColor, getInputFieldBaseStyles } from "@/ui/core/TextField";
import { HStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface TextAreaProps extends BaseViewProps {
  autoCapitalize?: TextInputProps["autoCapitalize"];
  autoComplete?: TextInputProps["autoComplete"];
  errorMessage?: string;
  hasError?: boolean;
  isAutoCorrect?: TextInputProps["autoCorrect"];
  isAutofocus?: boolean;
  isBorderless?: boolean;
  isBottomSheetTextArea?: boolean;
  isDisabled?: boolean;
  isEditable?: boolean;
  maxLength?: number;
  onChange: (value: string) => void;
  onPressIn?: () => void;
  placeholder: string;
  value?: null | string;
}

const StyledBottomSheetTextInput = styled(BottomSheetTextInput, {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  multiline: true,
  name: "TextArea",
  numberOfLines: 4,
  textAlignVertical: "top",
});

// eslint-disable-next-line no-restricted-syntax -- Core component needs passing of props to TextArea component
const TextArea = ({
  accessibilityLabel,
  autoCapitalize,
  autoComplete,
  errorMessage,
  hasError = false,
  isAutoCorrect,
  isAutofocus,
  isBorderless,
  isBottomSheetTextArea = false,
  isDisabled,
  isEditable = true,
  maxLength,
  onChange,
  onPressIn,
  placeholder,
  testID,
  value: initialValue,
}: TextAreaProps) => {
  const value = isNil(initialValue) ? "" : initialValue;

  const handleOnChange = (newValue: string) => {
    if (!isDisabled) {
      onChange(newValue);
    }
  };

  const baseTestID = joinTestIDs(testID, TextTestID.TextArea);
  const borderColor = hasError
    ? ColorToken.NotificationCritical
    : getBorderColor({ isBorderless });
  const baseStyles = getInputFieldBaseStyles({
    isBorderless,
  });

  const TextAreaComponent =
    isBottomSheetTextArea && !isWeb()
      ? StyledBottomSheetTextInput
      : BaseTextArea;

  const hasCharacterCount = !isNil(maxLength);
  const hasFooter = hasCharacterCount || Boolean(errorMessage);
  const backgroundColor = isDisabled
    ? ColorToken.NeutralBackgroundSecondary
    : ColorToken.NeutralBackgroundPrimary;

  return (
    <>
      <TextAreaComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...baseStyles}
        accessibilityLabel={accessibilityLabel}
        autoCapitalize={autoCapitalize}
        autoComplete={autoComplete}
        autoCorrect={isAutoCorrect}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={isAutofocus}
        autoFocusNative={isAutofocus}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        disabled={isDisabled}
        // editable differs from disabled in that disabled ignores press events entirely
        // editable={false} still invokes onPressIn / onPressOut / other event handlers (useful for triggering a MultiSelectModal)
        // in terms of styling here, disabled also sets a background color, while editable={false} does not
        // Platform-specific: native uses 'editable', web uses 'readOnly'
        // eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/naming-convention
        {...(Platform.OS !== "web" && { editable: isEditable })}
        // eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/naming-convention
        {...(Platform.OS === "web" && { readOnly: !isEditable })}
        focusStyle={{
          borderColor: hasError
            ? ColorToken.NotificationCritical
            : ColorToken.Primary100,
          borderWidth: 1,
        }}
        height={TextAreaDefaultHeight}
        // web-only: override Tamagui's default gray hover border with the current border color (no-op on native)
        hoverStyle={{ borderColor }}
        maxLength={maxLength}
        onChangeText={handleOnChange}
        onPressIn={onPressIn}
        placeholder={placeholder}
        placeholderTextColor={ColorToken.NeutralTertiary}
        testID={baseTestID}
        value={value}
        verticalAlign="top"
      />
      {hasFooter && (
        <HStack
          justifyContent={errorMessage ? "space-between" : "flex-end"}
          paddingTop={6}
        >
          {errorMessage && (
            <Text
              color={ColorToken.NotificationCriticalText}
              testID={joinTestIDs(baseTestID, "error-message")}
              variant="caption"
            >
              {errorMessage}
            </Text>
          )}
          {hasCharacterCount && (
            <Text
              color={ColorToken.NeutralSecondary}
              testID={joinTestIDs(baseTestID, "character-count")}
              variant="caption"
            >
              {value.length}/{maxLength}
            </Text>
          )}
        </HStack>
      )}
    </>
  );
};

export type { TextAreaProps };
export default TextArea;
